<script setup lang="ts">
import { inject, onMounted, ref, watch } from 'vue';

import { RouterView } from 'vue-router';
import AppFooter from '@/components/AppFooter.vue';
import AppNav from '@/components/AppNav.vue';
import MaintenacePage from '@/components/MaintenacePage.vue';
import { ui } from '@/configuration';

import type { Announcement, ApiService, GetTermsResponse } from '@/services/api';
import { useAuthStore } from '@/stores/auth';

const authStore = useAuthStore();

const api = inject<ApiService>('api');
if (!api) {
  throw new Error('API instance not provided');
}

const {
  login: { manageTermsAndConditions },
  features: { hasAnnouncements } = {},
} = ui;

const showTerms = ref(false);
const terms = ref<GetTermsResponse>();
const announcements = ref<Announcement[]>([]);

if (hasAnnouncements) {
  onMounted(async () => {
    const response = await api.getAnnouncements();
    if ('error' in response) {
      return;
    }

    announcements.value = response.announcements;
  });
}

const manageTerms = async () => {
  const fetchedTerms = await api.getTerms();
  if ('error' in fetchedTerms) {
    throw new Error(`Error managing Terms and Conditions: ${fetchedTerms.error}`);
  }

  if (!fetchedTerms.agreement) {
    showTerms.value = true;
  }

  terms.value = fetchedTerms;
  authStore.loginTermsUrl = fetchedTerms.url;
};

const acceptTerms = async () => {
  if (!terms.value) {
    return;
  }

  const response = await api.acceptTerms(terms.value.id);
  if ('error' in response) {
    throw new Error(`Error accepting Terms and Conditions: ${response.error}`);
  }

  showTerms.value = false;
};

if (manageTermsAndConditions) {
  watch(
    () => authStore.isLoggedIn,
    (loggedIn) => {
      if (loggedIn) {
        manageTerms();
      }
    },
    { immediate: true },
  );
}
</script>

<template>
  <template v-if="ui">
    <header class="sticky top-0 z-50">
      <el-row :gutter="0" :offset="0" style="" class="flex items-center justify-center">
        <el-col :xs="24" :sm="24" :md="24" :lg="22" :xl="17" :offset="0">
          <AppNav />
        </el-col>
      </el-row>
    </header>

    <el-row
      v-if="announcements.length"
      :gutter="0"
      :offset="0"
      class="flex items-center justify-center"
    >
      <el-col :xs="24" :sm="24" :md="24" :lg="22" :xl="17" :offset="0">
        <el-alert
          v-for="announcement in announcements"
          :key="announcement.id"
          :title="announcement.message"
          type="warning"
          show-icon
          class="mb-1"
        />
      </el-col>
    </el-row>

    <el-row :gutter="0" :offset="0" style="" class="flex items-center justify-center">
      <el-col :xs="24" :sm="24" :md="24" :lg="22" :xl="17" :offset="0">
        <main class="">
          <RouterView />
        </main>
      </el-col>
    </el-row>

    <footer>
      <AppFooter />
    </footer>
  </template>

  <template v-else>
    <MaintenacePage />
  </template>

  <el-dialog v-model="showTerms" width="50%" center class="mt-4 mb-4" :show-close="false" :close-on-click-modal="false">
    <template #header>
      <h2 class="break-normal">Terms And Conditions</h2>
    </template>

    <div>
      <p class="font-bold">{{ terms?.description }}</p>
      <pre class="whitespace-pre-wrap py-4 mt-3">{{ terms?.body }}</pre>

      <br />

      <a :href="terms?.url" class="break-normal underline text-blue-600" target="_blank noreferer">
        Terms & Conditions
      </a>
    </div>

    <template #footer>
      <span class="dialog-footer">
        <el-button type="primary" @click="acceptTerms">Accept Terms</el-button>
        <el-button type="info" @click="authStore.logout">Logout</el-button>
      </span>
    </template>
  </el-dialog>
</template>

<style>
.el-dialog {
  width: unset;
  max-width: var(--el-dialog-width);
}

.el-dialog .el-dialog__body {
  max-height: calc(100svh - 350px);
  overflow: auto;
}
</style>
